/**
 * Config Context
 *
 * @module ConfigContext
 * @description React context that provides centralized configuration to all components.
 * Ensures frontend uses the same values as backend (no hardcodes).
 *
 * ## Why This Exists:
 * To eliminate hardcoded values in frontend and ensure consistency with backend.
 * All configurable values come from /api/config endpoint.
 *
 * ## Available Configuration:
 * - **vault**: Activation delay, mining threshold, NFT mint SOL
 * - **investment**: Min/max amounts, activation delay
 * - **marketplace**: Platform fee, listing price limits
 * - **referral**: CASCADE rates (L1: 10%, L2: 6%, L3: 3%), treasury tax
 * - **tokenomics**: Total supply (21M), Bitcoin vault unlock (10%)
 *
 * ## Usage:
 * ```tsx
 * import { useConfig } from './contexts/ConfigContext'
 *
 * function MyComponent() {
 *   const config = useConfig()
 *
 *   return (
 *     <div>
 *       Total Supply: {config.tokenomics.totalSupply.toLocaleString()}
 *       Treasury Tax: {config.referral.treasuryTaxPercent}%
 *     </div>
 *   )
 * }
 * ```
 *
 * ## Implementation Notes:
 * - Config is fetched once on app load and cached
 * - Uses DEFAULT_CONFIG while API response is pending
 * - DEFAULT_CONFIG must match backend defaults
 *
 * @see config.service.ts - Config fetching and types
 * @see config.controller.ts (backend) - API endpoint
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AppConfig, DEFAULT_CONFIG, fetchAppConfig } from '../services/config.service';

const ConfigContext = createContext<AppConfig>(DEFAULT_CONFIG);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    fetchAppConfig().then(setConfig);
  }, []);

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig(): AppConfig {
  return useContext(ConfigContext);
}

export default ConfigContext;
